import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { orientationOptionIds } from 'commons/constants';
import CtaButton from 'components/essentials/Cta/CtaButton';
import JBImage from 'components/essentials/JBImage';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import StandardModal from '../StandardModal';

// Re-exported from commons/constants (server-safe) for back-compat with existing importers.
export { orientationOptionIds };

const orientationImageMap = {
  default: {
    left: 'https://joybird2.imgix.net/orientation-left-bg.png',
    right: 'https://joybird2.imgix.net/orientation-right-bg.png',
  },
  16: {
    left: 'https://joybird2.imgix.net/option_images/swatches/290x290-laf.jpg',
    right: 'https://joybird2.imgix.net/option_images/swatches/290x290-raf.jpg',
  },
  70: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/l-sectional-left.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/l-sectional-right.jpg',
  },
  74: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/single-arm-chaise-left.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/single-arm-chaise-right.jpg',
  },
  85: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/bumper-chaise-left.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/bumper-chaise-right.jpg',
  },
  89: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/left-single-arm-chaise.png',
    right:
      'https://joybird2.imgix.net/option_images/swatches/right-single-arm-chaise.png',
  },
  117: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/left-sleeper-sectional.png',
    right:
      'https://joybird2.imgix.net/option_images/swatches/right-sleeper-sectional.png',
  },
  119: {
    left: 'https://joybird2.imgix.net/option_images/swatches/left.jpg',
    right: 'https://joybird2.imgix.net/option_images/swatches/r.jpg',
  },
  120: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/sectional-left-bumper.png',
    right:
      'https://joybird2.imgix.net/option_images/swatches/sectional-right-bumper.png',
  },
  122: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/louie-left-arm-v2.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/louie-left-arm-v2.jpg',
  },
  123: {
    left: 'https://joybird2.imgix.net/option_images/swatches/orientation-l.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/JB_U-Sofa%20Right%20Facing%20Bumper%2002.png',
  },
  153: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/JB0518-Graphic-Orientation-Chair-Left.jpg',
    right:
      'https://joybird2.imgix.net/option_images/swatches/JB0518-Graphic-Orientation-Chair-Right.jpg',
  },
  154: {
    left:
      'https://joybird2.imgix.net/option_images/swatches/left-single-arm-chaise.png',
    right:
      'https://joybird2.imgix.net/option_images/swatches/right-single-arm-chaise.png',
  },
};

const OrientationPDPModalContent = ({
  close,
  onOptionClicked,
  optionId,
  values,
}) => (
  <div className="flex mt-10 lg:mt-8 mb-16 max-[1023px]:flex-col lg:justify-center">
    <div className="relative bg-white rounded hover:cursor-pointer max-[1023px]:m-auto max-[1023px]:max-w-[500px] max-[767px]:m-auto max-[767px]:min-w-[.8vw] first:max-[1023px]:mb-8 first:lg:mr-8 group">
      <div className="min-h-[157px]">
        <JBImage
          className="rounded-tl rounded-tr h-auto w-full"
          src={
            orientationImageMap[optionId].left
              ? orientationImageMap[optionId].left
              : orientationImageMap.default.left
          }
          fit="crop"
          crop="center"
          width="345"
          height="173"
        />
      </div>

      <button
        type="button"
        className={classNames(
          '[background:none] border-0 relative min-h-[2rem] p-6 max-[767px]:ml-0 max-[767px]:p-4',
          '[&_ul]:mx-0 [&_ul]:my-3 [&_ul]:min-h-[9rem] [&_ul_li]:text-base [&_ul_li]:flex [&_ul_li]:pb-[.15rem] [&_ul_li]:list-none [&_ul_li_img]:pr-[.2rem] [&_ul_li_img]:mr-2',
          '[&_button]:bg-brand-lightest [&_button]:text-brand-dark [&_button]:tracking-[.3px] [&_button]:whitespace-nowrap [&_button]:lg:min-w-[20rem] [&_button:hover]:bg-brand-lighter [&_button:active]:bg-brand-lightest'
        )}
        onClick={() => {
          onOptionClicked(values[0].id);
          close();
        }}
      >
        <CtaButton
          disabled={false}
          data-xc="standard-plan-btn"
          type="button"
          block
          className="border border-solid border-brand-lightest group-hover:border-brand-dark pointer-events-none"
        >
          Select Left Orientation
        </CtaButton>
      </button>
    </div>

    <div className="relative bg-white rounded hover:cursor-pointer max-[1023px]:m-auto max-[1023px]:max-w-[500px] max-[767px]:m-auto max-[767px]:min-w-[.8vw] first:max-[1023px]:mb-8 first:lg:mr-8 group">
      <div className="min-h-[157px]">
        <JBImage
          className="rounded-tl rounded-tr h-auto w-full"
          src={
            orientationImageMap[optionId].right
              ? orientationImageMap[optionId].right
              : orientationImageMap.default.right
          }
          fit="crop"
          crop="center"
          width="345"
          height="173"
        />
      </div>

      <button
        type="button"
        className={classNames(
          '[background:none] border-0 relative min-h-[2rem] p-6 max-[767px]:ml-0 max-[767px]:p-4',
          '[&_ul]:mx-0 [&_ul]:my-3 [&_ul]:min-h-[9rem] [&_ul_li]:text-base [&_ul_li]:flex [&_ul_li]:pb-[.15rem] [&_ul_li]:list-none [&_ul_li_img]:pr-[.2rem] [&_ul_li_img]:mr-2',
          '[&_button]:bg-brand-lightest [&_button]:text-brand-dark [&_button]:tracking-[.3px] [&_button]:whitespace-nowrap [&_button]:lg:min-w-[20rem] [&_button:hover]:bg-brand-lighter [&_button:active]:bg-brand-lightest'
        )}
        onClick={() => {
          onOptionClicked(values[1].id);
          close();
        }}
      >
        <CtaButton
          disabled={false}
          data-xc="standard-plan-btn"
          type="button"
          block
          className="border border-solid border-brand-lightest group-hover:border-brand-dark pointer-events-none"
        >
          Select Right Orientation
        </CtaButton>
      </button>
    </div>
  </div>
);

const OrientationPDPModal = ({
  isOpen,
  close,
  onOptionClicked,
  values,
  optionId,
}) => (
  <StandardModal
    isOpen={isOpen}
    onClose={close}
    showOkButtonOnMobile={false}
    size="md"
    title="Product Orientation"
    titleAlignment="center"
  >
    <OrientationPDPModalContent
      close={close}
      onOptionClicked={onOptionClicked}
      optionId={optionId}
      values={values}
    />
  </StandardModal>
);

OrientationPDPModalContent.propTypes = {
  close: PropTypes.func,
  onOptionClicked: PropTypes.func,
  optionId: PropTypes.number,
  values: PropTypes.array,
};

OrientationPDPModal.propTypes = {
  close: PropTypes.func,
  isOpen: PropTypes.bool,
  onOptionClicked: PropTypes.func,
  optionId: PropTypes.number,
  values: PropTypes.array,
};

export default withBrowserOnly(OrientationPDPModal);
